/**
 * `/agents/:agentId` — Agent とのチャット画面。
 *
 * Spec #171 Phase 2: Agent Lucid (scene / UISpec + agent_lucid_scenes) を
 * 廃止し「普通のチャット」(`features/chat/ChatView`) に差し替えた
 * (決定 D1、提案者: 凜さん 2026-07-02)。
 *
 *   - 送信: 既存の `POST /v1/agent/:id/text` 経路のまま (Phase 1 でサーバが
 *     chat_messages への並存書き込み済み)。
 *   - 受信: Supabase Realtime の chat_messages INSERT 購読
 *     (useAgentChatMessages — 旧 useAgentLucidScenes の auto-reconnect 踏襲)。
 *
 * 既存の settings / trust / memory 詳細パネルは `/agents/:agentId/settings`
 * に据え置き (本ルートの子ルート)。旧 developer console SSE
 * (`/agents/:agentId/chat`、`routes/agents.$agentId.chat.tsx`) は主チャット
 * への統合が完了済みの重複だったため F-08 (提案者: 凜さん 2026-07-04) で削除。
 */

import { useEffect, useState } from 'react';
import {
  createFileRoute,
  Link,
  Outlet,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router';
import { ArrowLeft, Bot, Settings as SettingsIcon } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { AuthGate } from '@/components/AuthGate';
import { Button } from '@/components/ui/button';
import { AgentPreparingBanner } from '@/components/agents/AgentPreparingBanner';
import { AgentHaltedBanner } from '@/components/agents/AgentHaltedBanner';
import { ChatView } from '@/features/chat/ChatView';
import { AgentBrowserStage } from '@/features/agent-browser/AgentBrowserStage';
import { useAgent } from '@/hooks/useAgent';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * Agent 詳細画面を開いた直後に「作業環境を準備しています」インジケータを
 * 出す期間。Hermes 側 `quon_session_warmup` プラグインが裏で repo を再準備
 * している間、ユーザーに「AI がフリーズしたのではなく裏で進んでいる」と
 * 知らせるためのフェイク skeleton。NN/g skeleton guideline (1〜10 秒) の下寄せ。
 */
const PREPARING_BANNER_DURATION_MS = 6_000;

interface AgentChatEmptyStateProps {
  agentName: string | null;
}

/**
 * メッセージ 0 件のとき、誰に話しかけているかの最小ヒント (1 行) を出す。
 * Quon の禅原則に反しない最小注釈に留める (旧 Agent Lucid の empty state 踏襲)。
 */
function AgentChatEmptyState({ agentName }: AgentChatEmptyStateProps) {
  return (
    <div
      className="flex flex-1 items-center justify-center"
      data-testid="agent-chat-empty"
    >
      <p className="text-sm text-muted-foreground">
        {agentName ? `${agentName} と話す` : ' '}
      </p>
    </div>
  );
}

function AgentChatPage({ agentId }: { agentId: string }) {
  const { data: agent, isLoading, error } = useAgent(agentId);
  const navigate = useNavigate();
  // 中央ステージは md 以上でのみ表示 (`hidden md:flex`)。モバイル幅では
  // 見えていないのでライブビューのポーリングも止める (レビュー HIGH-3)。
  // useIsMobile の breakpoint (768px) は Tailwind md と一致。
  const isMobile = useIsMobile();

  // Agent ごとに 1 回だけ「準備中」インジケータを出す。
  // agentId が変わったら再度短く表示する (別 agent を開いた = 別 sandbox)。
  const [preparingVisible, setPreparingVisible] = useState(true);
  useEffect(() => {
    setPreparingVisible(true);
    const t = setTimeout(
      () => setPreparingVisible(false),
      PREPARING_BANNER_DURATION_MS,
    );
    return () => clearTimeout(t);
  }, [agentId]);

  // `/agents/:agentId/settings` は本ルートの子ルート。子がマッチしたら
  // `<Outlet />` に全面委譲する (これが無いと設定ボタンを押しても
  // URL だけ変わり画面が変わらない)。
  const childMatches = useChildMatches();
  if (childMatches.length > 0) {
    return <Outlet />;
  }

  return (
    <AppShell>
      <div className="flex h-full min-h-0 flex-col">
        {/* Thin header: 戻る / 名前 / 設定・チャット */}
        <header
          className="sticky top-0 z-10 flex items-center gap-2 border-b border-border/40 bg-background px-4 py-2"
          data-testid="agent-chat-header"
        >
          {/* D-5 (凜さん判定 2026-07-05): /agents リスト画面は削除済みの
              ため、戻り先はホーム (Self Lucid)。Agent の切替はサイドバーが担う。 */}
          <Link
            to="/lucid"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            aria-label="ホームに戻る"
          >
            <ArrowLeft className="size-4" />
          </Link>
          <Bot className="size-4 text-accent" aria-hidden />
          <div className="flex-1 min-w-0">
            {isLoading ? (
              <span className="text-sm text-muted-foreground">読み込み中...</span>
            ) : error ? (
              <span className="text-sm text-destructive">
                Agent を読み込めませんでした
              </span>
            ) : !agent ? (
              <span className="text-sm text-destructive">Agent が見つかりません</span>
            ) : (
              <span
                data-testid="agent-detail-name"
                className="text-sm font-medium truncate block"
              >
                {agent.name}
              </span>
            )}
          </div>
          <Button asChild variant="ghost" size="sm" title="設定">
            <Link to="/agents/$agentId/settings" params={{ agentId }}>
              <SettingsIcon className="size-4" />
              <span className="sr-only">設定</span>
            </Link>
          </Button>
        </header>

        {/* 活動予算が尽きて止まっていることの常時表示 (Spec #51)。
            7/29 の停止に 15 日間誰も気づかなかった実測を受けて追加。流れて消えない
            ことが要件なので、下の準備中インジケータとは違い自動では消さない。 */}
        <AgentHaltedBanner agentId={agentId} />

        {/* 「作業環境を準備しています」インジケータ (数秒で自動的に消える)。 */}
        <AgentPreparingBanner visible={preparingVisible} />

        {/* 本体: 中央ステージ (Agent ブラウザのライブビュー) + 右サイドチャット。
            spec #43 T4 (提案者: 凜さん 2026-07-14)。中央は将来いろいろなビューを
            載せる器で、今はブラウザのみ。狭い画面ではチャットを全幅にする。 */}
        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          {isLoading ? null : error || !agent ? (
            <div className="flex flex-1 items-center justify-center p-8">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void navigate({ to: '/lucid' });
                }}
              >
                ホームに戻る
              </Button>
            </div>
          ) : (
            <>
              {/* 中央ステージ: md 以上で表示。ブラウザ非稼働時はアイドル。
                  モバイルでは非表示なので enabled=false でポーリングを止める。 */}
              <div className="hidden min-h-0 min-w-0 flex-1 md:flex" data-testid="agent-center-stage">
                <AgentBrowserStage agentId={agentId} enabled={!isMobile} />
              </div>
              {/* 右サイドチャット (入力欄含む)。狭い画面では全幅。
                  flex-1 が無いと flex-col の main-axis で高さが確保されず
                  チャットのスクロールが壊れる (レビュー HIGH-2)。 */}
              <div className="flex min-h-0 w-full flex-1 flex-col border-border/40 md:w-[380px] md:flex-none md:shrink-0 md:border-l">
                <ChatView
                  thread={{ kind: 'agent', agentId }}
                  emptyState={<AgentChatEmptyState agentName={agent.name} />}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function GatedDetail() {
  const { agentId } = Route.useParams();
  return (
    <AuthGate>
      <AgentChatPage agentId={agentId} />
    </AuthGate>
  );
}

export const Route = createFileRoute('/agents/$agentId')({
  component: GatedDetail,
});
